import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaPlay, FaPause, FaExpand, FaCompress, FaVolumeUp, FaVolumeMute } from 'react-icons/fa';
import './LocationVideo.css';

const LocationVideo = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const videoRef = useRef(null);
  const containerRef = useRef(null);

  const videoPath = process.env.PUBLIC_URL + '/videos/localisation cabinet.mp4';

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  // Détecter iOS
  const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
           (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  };

  const toggleFullscreen = () => {
    if (isIOS() && videoRef.current) {
      // Sur iOS, utiliser webkitEnterFullscreen() directement sur la vidéo
      if (videoRef.current.webkitEnterFullscreen) {
        videoRef.current.webkitEnterFullscreen();
        setIsFullscreen(true);
      } else if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
        setIsFullscreen(true);
      }
    } else {
      // Sur autres plateformes, utiliser le container
      if (!document.fullscreenElement && !document.webkitFullscreenElement && !document.mozFullScreenElement) {
        const element = containerRef.current || videoRef.current;
        if (element) {
          if (element.requestFullscreen) {
            element.requestFullscreen();
          } else if (element.webkitRequestFullscreen) {
            element.webkitRequestFullscreen();
          } else if (element.mozRequestFullScreen) {
            element.mozRequestFullScreen();
          } else if (element.msRequestFullscreen) {
            element.msRequestFullscreen();
          }
          setIsFullscreen(true);
        }
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen();
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen();
        }
        setIsFullscreen(false);
      }
    }
  };

  // Écouter les changements de fullscreen
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFull = !!(document.fullscreenElement || 
                       document.webkitFullscreenElement || 
                       document.mozFullScreenElement || 
                       document.msFullscreenElement);
      setIsFullscreen(isFull);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  const handleVideoClick = () => {
    togglePlay();
  };

  const handleMouseEnter = () => {
    setShowControls(true);
  };

  const handleMouseLeave = () => {
    if (isPlaying) {
      setTimeout(() => setShowControls(false), 2000);
    }
  };

  const handleVideoEnd = () => {
    setIsPlaying(false);
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
    }
  };

  const handleVideoPlay = () => {
    setIsPlaying(true);
  };

  const handleVideoPause = () => {
    setIsPlaying(false);
  };

  // Forcer la meilleure qualité vidéo sur iOS
  useEffect(() => {
    if (videoRef.current && isIOS()) {
      // Forcer le chargement de la meilleure qualité
      videoRef.current.load();
      
      // Écouter le chargement pour s'assurer de la meilleure qualité
      const handleLoadedMetadata = () => {
        if (videoRef.current) {
          // Forcer la résolution maximale disponible
          videoRef.current.playbackRate = 1.0;
        }
      };
      
      // Copier la référence pour le cleanup
      const videoElement = videoRef.current;
      
      if (videoElement) {
        videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);
      }
      
      return () => {
        if (videoElement) {
          videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
        }
      };
    }
  }, []);

  return (
    <motion.section 
      id="localisation" 
      className="location-video-section"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      variants={{
        hidden: { opacity: 0, y: 50 },
        visible: { 
          opacity: 1, 
          y: 0,
          transition: { duration: 0.8, ease: "easeOut" }
        }
      }}
    >
      <div className="container">
        <div className="section-header">
          <motion.h2 className="section-title centered-title" variants={{
            hidden: { opacity: 0, y: 20 },
            visible: { opacity: 1, y: 0, transition: { delay: 0.2 } }
          }}>
            <FaMapMarkerAlt /> Localisation
          </motion.h2>
        </div>

        <motion.div 
          className="video-container"
          ref={containerRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          variants={{
            hidden: { opacity: 0, scale: 0.95 },
            visible: { 
              opacity: 1, 
              scale: 1,
              transition: { delay: 0.5, duration: 0.6 }
            }
          }}
        >
          <div className="video-wrapper">
            <video
              ref={videoRef}
              className="location-video"
              src={videoPath}
              onClick={handleVideoClick}
              onEnded={handleVideoEnd}
              onPlay={handleVideoPlay}
              onPause={handleVideoPause}
              muted={isMuted}
              playsInline
              preload="auto"
              controls={false}
              aria-label="Vidéo de localisation du cabinet dentaire"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain'
              }}
            >
              <source src={videoPath} type="video/mp4" />
              Votre navigateur ne supporte pas la lecture de vidéos.
            </video>

            {/* Overlay avec contrôles */}
            <div className={`video-overlay ${showControls ? 'show' : ''}`}>
              {!isPlaying && (
                <motion.button
                  className="play-button-overlay"
                  onClick={togglePlay}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  aria-label="Lire la vidéo"
                >
                  <FaPlay />
                </motion.button>
              )}

              {/* Contrôles de la vidéo */}
              <div className={`video-controls ${showControls ? 'visible' : ''}`}>
                <button
                  className="control-btn play-pause-btn"
                  onClick={togglePlay}
                  aria-label={isPlaying ? "Pause" : "Lecture"}
                >
                  {isPlaying ? <FaPause /> : <FaPlay />}
                </button>

                <button
                  className="control-btn mute-btn"
                  onClick={toggleMute}
                  aria-label={isMuted ? "Activer le son" : "Couper le son"}
                >
                  {isMuted ? <FaVolumeMute /> : <FaVolumeUp />}
                </button>

              {!isIOS() && (
                <button
                  className="control-btn fullscreen-btn"
                  onClick={toggleFullscreen}
                  aria-label={isFullscreen ? "Quitter le plein écran" : "Plein écran"}
                >
                  {isFullscreen ? <FaCompress /> : <FaExpand />}
                </button>
              )}
              {isIOS() && (
                <button
                  className="control-btn fullscreen-btn"
                  onClick={toggleFullscreen}
                  aria-label="Plein écran iOS"
                >
                  <FaExpand />
                </button>
              )}
              </div>
            </div>

            {/* Badge de localisation */}
            <div className="location-badge">
              <FaMapMarkerAlt />
              <span>Résidence Najd, Tanger</span>
            </div>
          </div>

          {/* Informations supplémentaires */}
          <div className="location-info">
            <div className="location-details">
              <h3>Adresse complète</h3>
              <p>Riad Tetouan, Rue Marrakech, Résidence Najd, 4ème étage, Appartement 36, Tanger</p>
              <a 
                href="https://www.google.com/maps/place/Serenity+Dental+Clinic/@35.7678334,-5.8042277,925m/data=!3m1!1e3!4m6!3m5!1s0xd0b810d32d5d7c7:0x3e507fa6b27c86b3!8m2!3d35.7678359!4d-5.8043202!16s%2Fg%2F11z1rm6jyw!5m1!1e2!18m1!1e1?entry=ttu&g_ep=EgoyMDI2MDMyMy4xIKXMDSoASAFQAw%3D%3D"
                target="_blank"
                rel="noopener noreferrer"
                className="map-link"
              >
                <FaMapMarkerAlt /> Voir sur Google Maps
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default LocationVideo;

